import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { walletsService } from "@/services/walletsService";
import type {
  AdminWalletAdjustDto,
  WalletTransactionFilterParams,
  WithdrawalRequestDto,
} from "@/types/wallet.dto";
import { mapWalletTransactionsToRows } from "@/lib/mappers/account-mappers";
import { queryKeys } from "./keys";

export function useMyWallet(enabled = true) {
  return useQuery({
    queryKey: queryKeys.wallets.my,
    queryFn: () => walletsService.getMyWallet(),
    enabled,
  });
}

export function useAdminUserWallet(userId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.wallets.adminUser(userId ?? ""),
    queryFn: () => walletsService.getUserWallet(userId!),
    enabled: enabled && Boolean(userId),
  });
}

export function useWalletTransactions(
  walletId: number,
  filter: WalletTransactionFilterParams = { pageSize: 50 },
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.wallets.transactions(walletId, filter),
    queryFn: async () => {
      const result = await walletsService.getTransactions(walletId, filter);
      return {
        ...result,
        items: mapWalletTransactionsToRows(result.items),
      };
    },
    enabled: enabled && walletId > 0,
  });
}

export function useMyWalletTransactions(
  filter: WalletTransactionFilterParams = { pageSize: 20 },
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.wallets.myTransactions(filter),
    queryFn: () => walletsService.getMyTransactions(filter),
    enabled,
  });
}

export function useAdminWalletTransactions(
  filter: WalletTransactionFilterParams = { pageSize: 20 },
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.wallets.adminTransactions(filter),
    queryFn: () => walletsService.getAdminTransactions(filter),
    enabled,
  });
}

export function useWithdrawMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ walletId, dto }: { walletId: number; dto: WithdrawalRequestDto }) =>
      walletsService.withdraw(walletId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.wallets.my });
    },
  });
}

export function useAdminAdjustWalletMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: AdminWalletAdjustDto) => walletsService.adminAdjust(dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.wallets.adminUser(variables.userId) });
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useTransferPreviewMutation() {
  return useMutation({
    mutationFn: (memberCode: string) => walletsService.previewTransferRecipient(memberCode),
  });
}
